import { AlertCircle, Check } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/ui';
import { type InvoicePayUiState, type PublicInvoicePay } from '@entities/invoice-pay';
import { useFitMiddleEllipsis } from '@shared/lib/use-fit-middle-ellipsis';
import { formatDateTime } from '@shared/lib/format-date';
import { cn } from '@shared/lib/utils';
import { CopyIconButton } from './copy-icon-button';
import { ConfirmationsCounter } from './confirmations-counter';
import { ExpiresTimer } from './expires-timer';
import { InvoiceActivityLoader } from './invoice-activity-loader';
import { INVOICE_QR_SIZE, InvoiceQr, InvoiceQrImage } from './invoice-qr';

interface InvoicePayCardBProps {
  uiState: InvoicePayUiState;
  invoice: PublicInvoicePay | null;
  loadError: boolean;
  isProceeding: boolean;
  isChecking: boolean;
  checkCooldownLeft: number;
  selectedCurrency: string;
  selectedNetwork: string;
  expiresFormatted: string | null;
  expiresUrgent?: boolean;
  addressCopied?: boolean;
  txidCopied?: boolean;
  idCopied?: boolean;
  onSelectCurrency: (code: string) => void;
  onSelectNetwork: (code: string) => void;
  onProceed: () => void;
  onForceCheck: () => void;
  onCopyAddress: () => void;
  onCopyTxid: () => void;
  onCopyId: () => void;
  onGoBack: () => void;
  onRetry: () => void;
  onContactSupport: () => void;
}

const addressPanelClass = cn(
  'mx-auto flex w-full min-w-0 max-w-[468px] flex-col items-center rounded-lg border p-4 shadow-xs',
  'border-[color:var(--base-input)] bg-[var(--custom-background-dark-input-30)]'
);

const txidPanelClass = cn(
  'flex min-h-[72px] w-full min-w-[320px] max-w-[468px] flex-col justify-center gap-1 rounded-lg border p-4 shadow-xs',
  'border-[color:var(--base-input)] bg-[var(--custom-background-dark-input-30)]'
);

export function InvoicePayCardB(props: InvoicePayCardBProps) {
  const {
    uiState,
    invoice,
    loadError,
    isProceeding,
    isChecking,
    checkCooldownLeft,
    selectedCurrency,
    selectedNetwork,
    expiresFormatted,
    expiresUrgent,
    addressCopied,
    txidCopied,
    idCopied,
    onSelectCurrency,
    onSelectNetwork,
    onProceed,
    onForceCheck,
    onCopyAddress,
    onCopyTxid,
    onCopyId,
    onGoBack,
    onRetry,
    onContactSupport,
  } = props;

  return (
    <section
      className={cn(
        'w-full rounded-b-xl border-x border-b border-t-0 bg-background shadow-sm',
        'border-[color:var(--custom-outline)]',
        'px-5 pb-9 pt-7 sm:px-9'
      )}
    >
      {uiState === 'DETAILS' && invoice ? (
        <DetailsBody
          invoice={invoice}
          selectedCurrency={selectedCurrency}
          selectedNetwork={selectedNetwork}
          isProceeding={isProceeding}
          expiresFormatted={expiresFormatted}
          expiresUrgent={expiresUrgent}
          onSelectCurrency={onSelectCurrency}
          onSelectNetwork={onSelectNetwork}
          onProceed={onProceed}
        />
      ) : null}

      {uiState === 'PAYMENT' && invoice ? (
        <PaymentBody
          invoice={invoice}
          isChecking={isChecking}
          checkCooldownLeft={checkCooldownLeft}
          expiresFormatted={expiresFormatted}
          expiresUrgent={expiresUrgent}
          addressCopied={addressCopied}
          onForceCheck={onForceCheck}
          onCopyAddress={onCopyAddress}
        />
      ) : null}

      {(uiState === 'PAYMENT_DETECTED' || uiState === 'CONFIRMING') && invoice ? (
        <ConfirmingBody
          invoice={invoice}
          showAddressCheck={uiState === 'PAYMENT_DETECTED'}
          expiresFormatted={expiresFormatted}
          expiresUrgent={expiresUrgent}
          addressCopied={addressCopied}
          onCopyAddress={onCopyAddress}
        />
      ) : null}

      {uiState === 'SUCCESS' && invoice ? (
        <SuccessBody
          invoice={invoice}
          txidCopied={txidCopied}
          onCopyTxid={onCopyTxid}
          onGoBack={onGoBack}
        />
      ) : null}

      {uiState === 'EXPIRED' && invoice ? (
        <ExpiredBody invoice={invoice} onGoBack={onGoBack} />
      ) : null}

      {uiState === 'ERROR' ? (
        <ErrorBody
          invoice={invoice}
          loadError={loadError}
          idCopied={idCopied}
          onCopyId={onCopyId}
          onGoBack={onGoBack}
          onRetry={onRetry}
          onContactSupport={onContactSupport}
        />
      ) : null}
    </section>
  );
}


function DetailsBody({
  invoice,
  selectedCurrency,
  selectedNetwork,
  isProceeding,
  expiresFormatted,
  expiresUrgent,
  onSelectCurrency,
  onSelectNetwork,
  onProceed,
}: {
  invoice: PublicInvoicePay;
  selectedCurrency: string;
  selectedNetwork: string;
  isProceeding: boolean;
  expiresFormatted: string | null;
  expiresUrgent?: boolean;
  onSelectCurrency: (code: string) => void;
  onSelectNetwork: (code: string) => void;
  onProceed: () => void;
}) {
  const { t } = useTranslation('invoice-pay');
  const currencyOption = invoice.availableCurrencies.find((c) => c.code === selectedCurrency)
    ?? invoice.availableCurrencies[0];
  const networks = currencyOption?.networks ?? [];

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium text-foreground">{t('assetNetwork')}</p>

        <Select
          value={selectedCurrency}
          onValueChange={onSelectCurrency}
          disabled={isProceeding}
        >
          <SelectTrigger className="h-10 text-muted-foreground" aria-label={t('selectCurrency')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {invoice.availableCurrencies.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                <span className="flex items-center gap-2">
                  {c.iconUrl ? <img src={c.iconUrl} alt="" className="size-5" /> : null}
                  {c.code}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedNetwork}
          onValueChange={onSelectNetwork}
          disabled={isProceeding || networks.length === 0}
        >
          <SelectTrigger className="h-10 text-muted-foreground" aria-label={t('selectNetwork')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {networks.map((n) => (
              <SelectItem key={n.code} value={n.code}>
                {n.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="button"
        className="h-10 w-full rounded-lg shadow-xs active:scale-[0.99]"
        loading={isProceeding}
        disabled={!selectedCurrency || !selectedNetwork}
        onClick={onProceed}
      >
        {t('proceedToPayment')}
      </Button>

      {expiresFormatted ? (
        <div className="flex justify-center">
          <ExpiresTimer formatted={expiresFormatted} isUrgent={expiresUrgent} />
        </div>
      ) : null}
    </div>
  );
}

function WarningBanner() {
  const { t } = useTranslation('invoice-pay');
  return (
    <div className="flex gap-3 rounded-lg border border-[color:var(--custom-destructive-20)] bg-popover px-4 py-3">
      <AlertCircle className="size-5 shrink-0 text-fail-foreground" aria-hidden />
      <p className="whitespace-pre-line text-sm text-fail-foreground">{t('sendOnlyWarning')}</p>
    </div>
  );
}

/** Address text + trailing 20px icon + 4px gap. */
const ADDRESS_TRAILING_RESERVE_PX = 24;

function AddressLine({
  address,
  copied,
  showCheck,
  onCopy,
  copyLabel,
  align = 'start',
}: {
  address: string;
  copied?: boolean;
  showCheck?: boolean;
  onCopy: () => void;
  copyLabel: string;
  align?: 'start' | 'center';
}) {
  const { text, widthRef } = useFitMiddleEllipsis(address, ADDRESS_TRAILING_RESERVE_PX);

  return (
    <div
      ref={widthRef}
      className={cn(
        'flex h-5 w-full min-w-0 items-center font-sans text-sm font-medium tracking-normal text-base-card-foreground',
        align === 'center' ? 'justify-center' : 'justify-start'
      )}
    >
      <div className="inline-flex max-w-full min-w-0 items-center gap-1">
        <button
          type="button"
          onClick={onCopy}
          title={address}
          aria-label={address}
          className={cn(
            'min-w-0 cursor-pointer overflow-hidden whitespace-nowrap align-middle',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          {text}
        </button>
        {showCheck ? (
          <span className="inline-flex size-5 shrink-0 items-center justify-center text-success-foreground">
            <Check className="size-5" aria-hidden />
          </span>
        ) : (
          <CopyIconButton copied={copied} onClick={onCopy} label={copyLabel} />
        )}
      </div>
    </div>
  );
}

function AddressBlock({
  invoice,
  showCheck,
  addressCopied,
  onCopyAddress,
  addressLabelKey = 'recipientAddress',
}: {
  invoice: PublicInvoicePay;
  showCheck?: boolean;
  addressCopied?: boolean;
  onCopyAddress: () => void;
  addressLabelKey?: 'recipientAddress' | 'receivingWallet';
}) {
  const { t } = useTranslation('invoice-pay');
  const address = invoice.address ?? '';
  const qrValue = address;

  return (
    <div className={addressPanelClass}>
      {invoice.qrUrl ? (
        <InvoiceQrImage src={invoice.qrUrl} address={address} size={INVOICE_QR_SIZE} />
      ) : qrValue ? (
        <InvoiceQr value={qrValue} size={INVOICE_QR_SIZE} />
      ) : null}

      <div className="mt-4 flex w-full min-w-0 flex-col items-center gap-2">
        <p className="flex h-4 w-full shrink-0 items-center justify-center self-stretch font-sans text-xs font-normal tracking-normal text-base-muted-foreground">
          {t(addressLabelKey)}
        </p>
        <AddressLine
          address={address}
          copied={addressCopied}
          showCheck={showCheck}
          onCopy={onCopyAddress}
          copyLabel={t('copied')}
          align="center"
        />
      </div>
    </div>
  );
}

function PaymentBody({
  invoice,
  isChecking,
  checkCooldownLeft,
  expiresFormatted,
  expiresUrgent,
  addressCopied,
  onForceCheck,
  onCopyAddress,
}: {
  invoice: PublicInvoicePay;
  isChecking: boolean;
  checkCooldownLeft: number;
  expiresFormatted: string | null;
  expiresUrgent?: boolean;
  addressCopied?: boolean;
  onForceCheck: () => void;
  onCopyAddress: () => void;
}) {
  const { t } = useTranslation('invoice-pay');

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4">
        {invoice.status === 'underpaid' && invoice.remainingAmount ? (
          <p className="text-sm font-normal text-fail-foreground">
            {t('underpaidHint', {
              paidAmount: invoice.paidAmount,
              amount: invoice.amount,
              remainingAmount: invoice.remainingAmount,
            })}
          </p>
        ) : null}

        <AddressBlock
          invoice={invoice}
          addressCopied={addressCopied}
          onCopyAddress={onCopyAddress}
        />

        <WarningBanner />
      </div>

      <div className="mt-7 flex flex-col gap-2">
        <div className="relative overflow-hidden rounded-lg">
          <Button
            type="button"
            className="h-10 w-full rounded-lg shadow-xs active:scale-[0.99]"
            loading={isChecking}
            disabled={checkCooldownLeft > 0}
            onClick={onForceCheck}
          >
            {t('iveSentPayment')}
          </Button>
          {checkCooldownLeft > 0 && !isChecking ? (
            <div
              className="pointer-events-none absolute inset-0 z-[2] overflow-hidden rounded-lg"
              aria-hidden
            >
              <div className="h-full w-full origin-left animate-check-cooldown bg-white/25 dark:bg-white/20" />
            </div>
          ) : null}
        </div>
        {checkCooldownLeft > 0 ? (
          <p className="text-center text-sm font-normal text-base-muted-foreground">
            {t('checkCooldownHint')}
          </p>
        ) : null}
      </div>

      <div className="my-7 h-px w-full bg-[var(--base-input)]" aria-hidden />

      <div className="flex flex-col items-center gap-1 sm:flex-row sm:justify-center sm:gap-3">
        <ExpiresTimer formatted={expiresFormatted} isUrgent={expiresUrgent} />
        {invoice.confirmations.required > 0 ? (
          <>
            <span className="hidden text-base-muted-foreground sm:inline" aria-hidden>
              |
            </span>
            <ConfirmationsCounter
              current={invoice.confirmations.current}
              required={invoice.confirmations.required}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

function ConfirmingBody({
  invoice,
  showAddressCheck,
  expiresFormatted,
  expiresUrgent,
  addressCopied,
  onCopyAddress,
}: {
  invoice: PublicInvoicePay;
  showAddressCheck?: boolean;
  expiresFormatted: string | null;
  expiresUrgent?: boolean;
  addressCopied?: boolean;
  onCopyAddress: () => void;
}) {
  const { t } = useTranslation('invoice-pay');

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col items-center gap-3" role="status">
        <InvoiceActivityLoader />
        {invoice.confirmations.required > 0 ? (
          <ConfirmationsCounter
            current={invoice.confirmations.current}
            required={invoice.confirmations.required}
            className="text-center"
          />
        ) : null}
        <ExpiresTimer formatted={expiresFormatted} isUrgent={expiresUrgent} />
        <p className="sr-only">{t('checkingPayment')}</p>
      </div>

      <AddressBlock
        invoice={invoice}
        showCheck={showAddressCheck}
        addressCopied={addressCopied}
        onCopyAddress={onCopyAddress}
        addressLabelKey="receivingWallet"
      />
    </div>
  );
}

function SuccessBody({
  invoice,
  txidCopied,
  onCopyTxid,
  onGoBack,
}: {
  invoice: PublicInvoicePay;
  txidCopied?: boolean;
  onCopyTxid: () => void;
  onGoBack: () => void;
}) {
  const { t } = useTranslation('invoice-pay');
  const txid = invoice.txid ?? '';

  return (
    <div className="flex w-full flex-col items-center" role="status" aria-live="polite">
      <div className="flex size-12 items-center justify-center rounded-full bg-success-foreground/15 text-success-foreground shadow-[0_0_24px_rgba(34,197,94,0.35)]">
        <Check className="size-7" strokeWidth={2.5} />
      </div>
      <div className="mt-6 flex flex-col items-center gap-1 text-center">
        <p className="text-sm font-medium text-foreground">{t('paymentIsPaid')}</p>
        <p className="text-sm text-muted-foreground">{t('successfullyPaid')}</p>
      </div>

      {txid ? (
        <div className={cn(txidPanelClass, 'mt-6')}>
          <p className="flex h-4 items-center font-sans text-xs font-normal tracking-normal text-base-muted-foreground">
            {t('txid')}
          </p>
          <AddressLine
            address={txid}
            copied={txidCopied}
            onCopy={onCopyTxid}
            copyLabel={t('copied')}
          />
        </div>
      ) : null}

      <Button
        type="button"
        className="mt-7 h-10 w-full rounded-lg shadow-xs"
        onClick={onGoBack}
      >
        {t('goBack')}
      </Button>
    </div>
  );
}

function ExpiredBody({
  invoice,
  onGoBack,
}: {
  invoice: PublicInvoicePay;
  onGoBack: () => void;
}) {
  const { t } = useTranslation('invoice-pay');
  const expiredAt = invoice.expiresAt ? formatDateTime(invoice.expiresAt) : null;

  return (
    <div className="flex flex-col items-center gap-6" role="status" aria-live="polite">
      <div className="flex size-12 items-center justify-center rounded-full bg-fail-foreground/15 text-fail-foreground shadow-[0_0_24px_rgba(239,68,68,0.35)]">
        <AlertCircle className="size-7" />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="text-sm font-normal text-fail-foreground">{t('expiredTitle')}</p>
        {expiredAt ? (
          <p className="text-sm font-normal text-base-muted-foreground">
            {t('expiredAt', { date: expiredAt })}
          </p>
        ) : null}
      </div>
      <Button type="button" className="h-10 w-full rounded-lg shadow-xs" onClick={onGoBack}>
        {t('goBack')}
      </Button>
    </div>
  );
}

function ErrorBody({
  invoice,
  loadError,
  idCopied,
  onCopyId,
  onGoBack,
  onRetry,
  onContactSupport,
}: {
  invoice: PublicInvoicePay | null;
  loadError: boolean;
  idCopied?: boolean;
  onCopyId: () => void;
  onGoBack: () => void;
  onRetry: () => void;
  onContactSupport: () => void;
}) {
  const { t } = useTranslation('invoice-pay');

  return (
    <div className="flex flex-col items-center gap-6" role="status" aria-live="polite">
      <div className="flex size-12 items-center justify-center rounded-full bg-fail-foreground/15 text-fail-foreground shadow-[0_0_24px_rgba(239,68,68,0.35)]">
        <AlertCircle className="size-7" />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="text-sm font-medium text-fail-foreground">
          {loadError ? t('failedToLoad') : t('errorTitle')}
        </p>
        {!loadError ? (
          <p className="text-sm text-muted-foreground">{t('errorDescription')}</p>
        ) : null}
      </div>

      {invoice?.id ? (
        <div className="flex items-center gap-1 font-mono text-sm">
          <span>{invoice.id}</span>
          <CopyIconButton copied={idCopied} onClick={onCopyId} label={t('copied')} />
        </div>
      ) : null}

      <div className="flex w-full flex-col gap-2">
        {loadError ? (
          <Button type="button" className="h-10 w-full rounded-lg" onClick={onRetry}>
            {t('retry')}
          </Button>
        ) : null}
        <Button type="button" className="h-10 w-full rounded-lg" onClick={onGoBack}>
          {t('goBack')}
        </Button>
        <Button type="button" variant="ghost" className="h-10 w-full" onClick={onContactSupport}>
          {t('contactSupport')}
        </Button>
      </div>
    </div>
  );
}

export function InvoiceSupportLine({ supportUrl }: { supportUrl: string }) {
  const { t } = useTranslation('invoice-pay');

  return (
    <p className="mt-6 text-center text-sm text-muted-foreground sm:mt-7">
      <Trans
        t={t}
        i18nKey="supportLine"
        components={{
          support: (
            <a
              href={supportUrl}
              className="underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          ),
        }}
      />
    </p>
  );
}
